import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import NextLink from 'next/link';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { EventType } from '@/analytics/types';
import type { BaseStoreRingData } from '@/app/[locale]/store/_types/types';
import Image from '@/app/components/Image';
import PriceInfo, { type PriceInfoProps } from '@/app/components/PriceInfo';

export interface StoreProductCardProps extends BaseStoreRingData {
  readonly className?: string;
  readonly priceInfo: PriceInfoProps;
}

export const StoreProductCard = ({
  className,
  finish,
  subcopy,
  productImg,
  lifeStyleImg,
  pdpPath,
  priceInfo,
}: StoreProductCardProps) => {
  const t = useTranslations();
  const router = useRouter();

  const handleClick = async (): Promise<void> => {
    await window.ouraAnalytics.track(EventType.CTAClicked, {
      cta: 'shop',
      location: 'carousel',
      path: pdpPath,
      action: 'go to pdp',
    });
    router.push(pdpPath);
  };

  return (
    <article
      key={finish}
      className={cx(
        'group mt-4 h-[240px] w-[240px] cursor-pointer md:mt-12 md:h-[336px] md:w-[240px] lg:h-[422px] lg:w-[360px]',
        className,
      )}
      data-cy="store-product-card"
      onClick={() => handleClick()}
    >
      <GridItem
        rowStart={{ sm: 1 }}
        colStart={{ sm: 1 }}
        className="relative pt-1 md:pt-8.5 lg:pt-7.5"
      >
        <Image
          localizedAlt={productImg.alt}
          className={cx(
            'z-10 mx-7.5 mb-0 aspect-square md:mx-2.5 md:mb-[31px] lg:mx-7.5 lg:mb-[24px]',
            'opacity-100 transition-all duration-1000 ease-in-out group-focus-within:md:opacity-0 group-hover:md:opacity-0',
            'h-[180px] w-[180px] md:h-[220px] md:w-[220px] lg:h-[300px] lg:w-[300px]',
          )}
          format="png"
          width={300}
          height={300}
          src={productImg.src}
          data-cy="product-card-product-image"
        />
      </GridItem>
      <GridItem
        rowStart={{ sm: 1 }}
        colStart={{ sm: 1 }}
        className="absolute top-0 aspect-square rounded-[40px] opacity-0 transition-all duration-1000 ease-in-out md:top-12 md:h-[336px] md:w-[240px] group-focus-within:md:opacity-100 group-hover:md:opacity-100 lg:h-[422px] lg:w-[360px]"
      >
        <Image
          localizedAlt={lifeStyleImg.alt}
          className="rounded-[40px] object-cover md:h-[336px] md:w-[240px] lg:h-[422px] lg:w-[360px]"
          format="jpg"
          width={300}
          height={422}
          src={lifeStyleImg.src}
          data-cy="product-card-lifestyle-image"
        />
      </GridItem>

      <GridItem rowStart={{ sm: 2 }} colStart={{ sm: 1 }} className="relative">
        <NextLink
          href={pdpPath}
          className="absolute left-[50%] translate-x-[-50%] text-base whitespace-nowrap transition-all duration-1000 ease-in-out will-change-[left,transform] md:px-6 group-focus-within:md:left-0 group-focus-within:md:translate-x-0 group-focus-within:md:text-sandstone-100 group-hover:md:left-0 group-hover:md:translate-x-0 group-hover:md:text-sandstone-100 lg:px-8 lg:text-2xl"
          data-cy="store-product-card-link"
        >
          <Typography
            Element="h3"
            color="inherit"
            data-cy="product-card-title"
            className="text-base lg:text-heading-sm"
          >
            {t(finish)}
          </Typography>
        </NextLink>
        {subcopy && (
          <Typography
            className={cx(
              'absolute top-[24px] left-[50%] translate-x-[-50%] text-xs text-gray-400 opacity-100 transition-all duration-1000 ease-in-out md:top-[23px] md:pb-1.5 group-focus-within:md:opacity-0 group-hover:md:opacity-0 lg:top-[31px] lg:pb-4 lg:text-base',
            )}
            data-cy="product-card-subcopy"
          >
            {t(subcopy)}
          </Typography>
        )}
        <PriceInfo
          className="absolute rounded-full bg-white px-4 py-2.5 opacity-0 transition-all duration-500 ease-in-out md:top-[-8px] md:right-4 md:translate-x-0 group-focus-within:md:opacity-100 group-hover:md:opacity-100"
          priceClassName="text-sm lg:text-base"
          dataCy="store-product-card-price"
          currencyCode={priceInfo.currencyCode}
          discountedPrice={priceInfo.discountedPrice}
          basePrice={priceInfo.basePrice}
          showDecimalsForWholeDollars
        />
      </GridItem>
    </article>
  );
};
